import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, matchPath, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { absoluteUrl, canonicalFor } from "utils/seo";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";

// Path -> page key. The components themselves are passed in as `pages`, so the browser
// can load each page as its own chunk (pages.lazy.jsx) while the prerender imports them
// all up front (entry-server.jsx). A new route needs an entry in both of those files.
export const routeTable = [
  { path: "/", page: "Homepage" },
  { path: "/contact", page: "Contact" },
  { path: "/health-benefits", page: "HealthBenefitsPage" },
  { path: "/antioxidant-power-detail", page: "AntioxidantPowerDetail" },
  { path: "/heart-health-detail", page: "HeartHealthDetail" },
  { path: "/events", page: "EventsPage" },
  { path: "/events/:slug", page: "EventDetailsPage" },
  { path: "/recipes", page: "RecipesPage" },
  { path: "/recipes/:id", page: "RecipeDetailsPage" },
  { path: "/blog-hub", page: "BlogHub" },
  { path: "/blog/:slug", page: "BlogDetail" },
  { path: "/urinary-tract-health-detail", page: "UrinaryTractHealthDetailPage" },
  { path: "/health-benefits/educational/immune-system-boost", page: "ImmuneSystemBoost" },
  { path: "/health-benefits/educational/digestive-wellness", page: "DigestiveWellness" },
  { path: "/health-benefits/educational/anti-inflammatory-effects", page: "AntiInflammatoryEffects" },
  { path: "/privacy-policy", page: "PrivacyPolicy" },
  { path: "/terms", page: "Terms" },
  { path: "/about", page: "About" },
];

// Page key for a pathname, or undefined when nothing matches (the 404 case).
export const pageKeyFor = (pathname) =>
  routeTable.find((route) => matchPath(route.path, pathname))?.page;

// ✅ Inline Google Analytics tracking (no extra files)
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag("config", "G-PY3PBRY3QP", {
        page_path: location.pathname + location.search,
      });
      console.log("📊 GA Page view:", location.pathname + location.search);
    } else {
      console.warn("⚠️ gtag not loaded yet, skipping page view");
    }
  }, [location]);

  return null; // no UI output
};

// Shown when a link is shared and the page has no image of its own.
const DEFAULT_SHARE_IMAGE = absoluteUrl("/assets/images/Homepage/cranharvest.jpg");

// One canonical + og:url for every route, always on the www origin.
// Pages must not set their own canonical; NotFound opts out via noindex.
// Also sets the default share image: a page's own og:image (rendered later) replaces it.
const CanonicalTag = () => {
  const { pathname } = useLocation();
  const canonical = canonicalFor(pathname);

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={DEFAULT_SHARE_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
  );
};

// Router-agnostic tree: BrowserRouter in the browser, StaticRouter when prerendering.
export const RouteTree = ({ pages }) => {
  return (
      <ErrorBoundary>
        <ScrollToTop />
        {/* ✅ Track GA pageviews on route change */}
        <PageTracker />
        <CanonicalTag />
        <Suspense fallback={null}>
          <Routes>
            {routeTable.map(({ path, page }) => {
              const Page = pages[page];
              return <Route key={path} path={path} element={<Page />} />;
            })}
            <Route path="/homepage" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
  );
};

const AppRoutes = ({ pages }) => {
  return (
    <BrowserRouter>
      <RouteTree pages={pages} />
    </BrowserRouter>
  );
};

export default AppRoutes;
